import { ROUTE } from '../../../../routes';
import type { WebsiteCardBody, WebsiteCardIntent, WebsiteCardSummary } from '../../card-summary';
import type { ReadStatus } from '../creatives/creatives-summary';

/**
 * What the Game Lists card says, from its hook's check of the Homepage (`games-check.ts`): whether anything waits to
 * be published there, and how many lists the page has and how many of them are hidden (when that is known). Pure: copy
 * is an id, mapped to its key in `games-copy.ts`.
 */

export type GamesCardInput = {
  status: ReadStatus;
  /** The Homepage has something to publish (shown without a number). */
  changed: boolean;
  /** The Homepage's lists and how many are hidden; null when they are not known (a Homepage the owner cannot arrange,
   *  whose arrangement the service does not give): the body is then empty. */
  lists: { total: number; hidden: number } | null;
};

export type GamesCopy =
  | { id: 'unpublished-changes' }
  | { id: 'lists-on-homepage'; n: number }
  | { id: 'lists-on-homepage-hidden'; n: number; m: number }
  | { id: 'review-and-publish' }
  | { id: 'open-game-lists' };

const OPEN_GAME_LISTS: WebsiteCardIntent<GamesCopy> = { copy: { id: 'open-game-lists' }, to: ROUTE.gameLists() };

/** The contract's body without a line: what the card shows when it has nothing it can say (as after a failed read,
 *  which the card does not show as an error either). */
const NO_LINE: WebsiteCardBody<GamesCopy> = { kind: 'failed' };

export const deriveGamesSummary = (input: GamesCardInput): WebsiteCardSummary<GamesCopy> => {
  const { status, changed, lists } = input;
  if (status !== 'ready') {
    return { chip: null, body: { kind: status }, primary: OPEN_GAME_LISTS, secondary: null };
  }
  // No Reorder lists on the card (the user, 2026-10-21): the lists are ordered in the tab's column of lists.
  if (changed) {
    return {
      chip: { tone: 'waiting', copy: { id: 'unpublished-changes' } },
      body: { kind: 'waiting', lines: [{ id: 'unpublished-changes' }] },
      primary: { copy: { id: 'review-and-publish' }, to: ROUTE.gameLists() },
      secondary: null,
    };
  }
  if (lists === null) return { chip: null, body: NO_LINE, primary: OPEN_GAME_LISTS, secondary: null };
  const { total: n, hidden: m } = lists;
  return {
    chip: null,
    body: {
      kind: 'context',
      line: m > 0 ? { id: 'lists-on-homepage-hidden', n, m } : { id: 'lists-on-homepage', n },
    },
    // No Add list on the card (the user, 2026-10-04), nor Reorder lists (2026-10-21): it opens the tab.
    primary: OPEN_GAME_LISTS,
    secondary: null,
  };
};
